import React, { useState } from 'react';
import { ArrowLeft, Sprout, AlertCircle, Loader } from 'lucide-react';
import { Button, GardenBackground } from '../components/ui';

export const KidLogin = ({ onNavigate, onLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    pin: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app/api';

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.pin) {
      newErrors.pin = 'PIN is required';
    } else if (formData.pin.length !== 4) {
      newErrors.pin = 'PIN must be 4 digits';
    } else if (!/^\d+$/.test(formData.pin)) {
      newErrors.pin = 'PIN must contain only numbers';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await fetch(`${API_BASE_URL}/auth/child-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username.toLowerCase(),
          pin: formData.pin
        })
      });

      const result = await response.json();

      if (result.success) {
        if (onLogin) {
          onLogin({
            user: {
              id: result.data.child.id,
              username: result.data.child.username,
              type: 'child'
            },
            child: result.data.child,
            family: result.data.family,
            accountType: 'child'
          });
        }
      } else {
        if (result.error.includes('username') || result.error.includes('not found')) {
          setErrors({ username: 'Username not found' });
        } else if (result.error.includes('PIN') || result.error.includes('password')) {
          setErrors({ pin: 'Incorrect PIN' });
        } else {
          setErrors({ general: result.error });
        }
      }
    } catch (error) {
      setErrors({ general: 'Unable to connect. Please try again.' });
    }

    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12">
      <GardenBackground />
      <div className="w-full max-w-xs relative z-10">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <img src="/logo.svg" alt="GrowthSprouts" className="w-14 h-14 mx-auto mb-3" />
            <h1 className="text-2xl text-gray-900 mb-1" style={{ fontFamily: "'DM Serif Display', serif" }}>Hey there!</h1>
            <p className="text-sm text-gray-600">Enter your username and secret PIN to see your garden</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Username
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-colors ${
                  errors.username ? 'border-red-300' : 'border-gray-200'
                }`}
                placeholder="your_username"
                disabled={isSubmitting}
                autoComplete="username"
              />
              {errors.username && (
                <p className="text-red-600 text-sm mt-1">{errors.username}</p>
              )}
            </div>

            {/* PIN */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                PIN
              </label>
              <input
                type="password"
                value={formData.pin}
                onChange={(e) => handleInputChange('pin', e.target.value)}
                maxLength={4}
                className={`w-full px-3 py-2.5 border rounded-xl text-sm text-center font-mono tracking-widest focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-colors ${
                  errors.pin ? 'border-red-300' : 'border-gray-200'
                }`}
                placeholder="- - - -"
                disabled={isSubmitting}
                autoComplete="current-password"
              />
              {errors.pin && (
                <p className="text-red-600 text-sm mt-1">{errors.pin}</p>
              )}
            </div>

            {errors.general && (
              <div className="bg-red-50 border border-red-100 rounded-xl p-3">
                <div className="flex items-center text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  {errors.general}
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              size="lg"
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Signing In...
                </>
              ) : (
                'Enter My Garden'
              )}
            </Button>
          </form>

          <div className="mt-4 text-center space-y-3">
            <p className="text-xs text-gray-400">
              Ask your parents if you forgot your username or PIN
            </p>
            <button
              type="button"
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors inline-flex items-center gap-1"
              onClick={() => onNavigate('welcome')}
              disabled={isSubmitting}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};